import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

const FULLSCREEN_EVENTS = [
  'fullscreenchange',
  'webkitfullscreenchange',
  'mozfullscreenchange',
  'MSFullscreenChange'
];

const TOGGLE_DELAY = 300;

export const useFullscreen = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timeoutRef = useRef(null);

  const getFullscreenElement = () => (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );

  const requestFullscreen = async (element) => {
    const requestMethod = 
      element.requestFullscreen ||
      element.webkitRequestFullscreen ||
      element.mozRequestFullScreen ||
      element.msRequestFullscreen;
    
    return requestMethod?.call(element);
  };

  const exitFullscreen = async () => {
    const exitMethod = 
      document.exitFullscreen ||
      document.webkitExitFullscreen ||
      document.mozCancelFullScreen ||
      document.msExitFullscreen;
    
    return exitMethod?.call(document);
  };

  const handleFullscreenError = (message, error) => {
    console.error(message, error);
    //toast.error(message);
    setIsTransitioning(false);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = getFullscreenElement();
      setIsFullscreen(!!fullscreenElement);
      setIsTransitioning(false);
    };

    // Initial check
    handleFullscreenChange();

    // Add event listeners
    FULLSCREEN_EVENTS.forEach(event => {
      document.addEventListener(event, handleFullscreenChange);
    });

    return () => {
      FULLSCREEN_EVENTS.forEach(event => {
        document.removeEventListener(event, handleFullscreenChange);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const enterFullScreen = async () => {
    try {
      if (!getFullscreenElement() && !isTransitioning) {
        setIsTransitioning(true);
        await requestFullscreen(document.documentElement);
        
        if (navigator.keyboard?.lock) {
          await navigator.keyboard.lock();
        }
      }
    } catch (error) {
      handleFullscreenError("Failed to enter full-screen mode", error);
    }
  };

  const handleExitFullScreen = async () => {
    try {
      if (getFullscreenElement() && !isTransitioning) {
        setIsTransitioning(true);
        await exitFullscreen();
        
        if (navigator.keyboard?.unlock) {
          navigator.keyboard.unlock();
        }
      }
    } catch (error) {
      handleFullscreenError("Failed to exit full-screen mode", error);
    }
  };

  const toggleFullScreen = () => {
    if (isTransitioning) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const fullscreenElement = getFullscreenElement();
    
    timeoutRef.current = setTimeout(() => {
      if (fullscreenElement) {
        handleExitFullScreen();
      } else {
        enterFullScreen();
      }
    }, TOGGLE_DELAY);
  };

  return {
    isFullscreen: !!getFullscreenElement(), // Direct DOM check
    isTransitioning,
    enterFullScreen,
    exitFullScreen: handleExitFullScreen,
    toggleFullScreen
  };
};